import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AppDateTimePicker from 'src/common/components/form/AppDateTimePicker';
import {
  PickersLayoutContentWrapper,
  PickersLayoutRoot,
  pickersLayoutClasses,
  usePickerLayout,
  type PickersLayoutProps,
} from '@mui/x-date-pickers/PickersLayout';
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { LanguageKeys } from 'src/common/constants/languages';
import { hasActiveListFilter } from 'src/helpers/listFilters';
import dayjs, { type Dayjs, toDayjs } from 'src/helpers/dayjs';
import { getEditionRegistrationPeriodDefaults } from 'src/helpers/participantList';
import {
  getEditionCompetitionOptions,
  getEditionDistanceIds,
  getEditionDistanceOptions,
} from 'src/helpers/signupTimeline';
import {
  EParticipantFilters,
  type ParticipantFilterValue,
} from 'src/interfaces/Participant';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

/** Shortcuts between calendar/clock content and Cancel/Next action bar. */
function FilterDateTimePickerLayout(props: PickersLayoutProps<Dayjs | null>) {
  const { toolbar, tabs, content, actionBar, shortcuts, ownerState } = usePickerLayout(props);

  return (
    <PickersLayoutRoot
      ownerState={ownerState}
      className={pickersLayoutClasses.root}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        [`& .${pickersLayoutClasses.shortcuts}`]: {
          order: 'unset',
          gridColumn: 'unset',
          gridRow: 'unset',
          display: 'flex',
          justifyContent: 'center',
          px: 1,
          py: 0.5,
          maxHeight: 'none',
          '& .MuiList-root': {
            flexDirection: 'row',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 0.5,
          },
        },
      }}
    >
      {toolbar}
      <PickersLayoutContentWrapper
        className={pickersLayoutClasses.contentWrapper}
        ownerState={ownerState}
      >
        {tabs}
        {content}
      </PickersLayoutContentWrapper>
      {shortcuts}
      {actionBar}
    </PickersLayoutRoot>
  );
}

interface Props {
  checkedInFilter: string[];
  competitionFilter: string[];
  distanceFilter: string[];
  editionFilter: string | null;
  registeredFrom: string | null;
  registeredTo: string | null;
  onChange: (key: EParticipantFilters, value: ParticipantFilterValue) => void;
}

function parseMultiSelectValue(raw: string | string[]): string[] {
  return typeof raw === 'string' ? (raw ? raw.split(',') : []) : raw;
}

type MultiFilterSelectProps = {
  id: string;
  label: string;
  value: string[];
  options: { value: string; label: string }[];
  onChange: (next: string[]) => void;
  clearAriaLabel: string;
};

function MultiFilterSelect({
  id,
  label,
  value,
  options,
  onChange,
  clearAriaLabel,
}: MultiFilterSelectProps) {
  const labelByValue = useMemo(
    () => new Map(options.map(o => [o.value, o.label])),
    [options],
  );
  const hasValue = value.length > 0;
  const [focused, setFocused] = useState(false);
  const shrink = hasValue || focused;

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    onChange(parseMultiSelectValue(event.target.value));
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
      <FormControl size="small" sx={{ minWidth: 0, flex: 1 }} focused={focused}>
        <InputLabel id={`${id}-label`} shrink={shrink}>
          {label}
        </InputLabel>
        <Select
          labelId={`${id}-label`}
          id={id}
          multiple
          displayEmpty
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          input={<OutlinedInput label={label} notched={shrink} />}
          renderValue={selected => {
            if (!selected.length) {
              return '';
            }
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map(idValue => (
                  <Chip
                    key={idValue}
                    label={labelByValue.get(idValue) ?? idValue}
                    size="small"
                  />
                ))}
              </Box>
            );
          }}
          MenuProps={{
            slotProps: {
              paper: {
                style: { maxHeight: 48 * 4.5 + 8 },
              },
            },
          }}
        >
          {options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {hasValue ? (
        <Tooltip title={clearAriaLabel}>
          <IconButton
            aria-label={clearAriaLabel}
            size="small"
            onClick={() => onChange([])}
            sx={{ mt: 0.5, flexShrink: 0 }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
}

const ParticipantFilterPopoverContent = ({
  checkedInFilter,
  competitionFilter,
  distanceFilter,
  editionFilter,
  registeredFrom,
  registeredTo,
  onChange,
}: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const [advancedOpen, setAdvancedOpen] = useState(
    () => hasActiveListFilter(registeredFrom) || hasActiveListFilter(registeredTo),
  );

  useEffect(() => {
    if (hasActiveListFilter(registeredFrom) || hasActiveListFilter(registeredTo)) {
      setAdvancedOpen(true);
    }
  }, [registeredFrom, registeredTo]);

  const { competitionsById, distancesById, editionsById, activeEditionId } = useSystemOptionsStore(
    systemOptionsState => systemOptionsState,
  );

  const editionId = editionFilter || activeEditionId || null;
  const edition = editionId ? editionsById?.[editionId] : undefined;

  const competitions = useMemo(
    () => getEditionCompetitionOptions(edition, competitionsById, { includeUnpublished: true }),
    [edition, competitionsById],
  );

  const distances = useMemo(
    () =>
      getEditionDistanceOptions(edition, {
        competitionIds: competitionFilter,
        distancesById,
        includeUnpublished: true,
      }),
    [edition, competitionFilter, distancesById],
  );

  const clearFilterLabel = t('form.clearFilter');
  const competitionOptions = useMemo(
    () => competitions.map(c => ({ value: c.id, label: c.i18n[currentLang] })),
    [competitions, currentLang],
  );
  const distanceOptions = useMemo(
    () => distances.map(d => ({ value: d.id, label: d.i18n[currentLang] })),
    [distances, currentLang],
  );
  const checkedOptions = useMemo(
    () => [
      { value: 'true', label: t('form.yes') },
      { value: 'false', label: t('form.no') },
    ],
    [t],
  );

  const handleCompetitionChange = (next: string[]) => {
    onChange(EParticipantFilters.competition, next);
    if (!next.length) {
      return;
    }
    const validDistanceIds = new Set(
      getEditionDistanceIds(edition, {
        competitionIds: next,
        distancesById,
        includeUnpublished: true,
      }),
    );
    const pruned = distanceFilter.filter(id => validDistanceIds.has(id));
    if (pruned.length !== distanceFilter.length) {
      onChange(EParticipantFilters.distance, pruned);
    }
  };

  const periodActive =
    hasActiveListFilter(registeredFrom) || hasActiveListFilter(registeredTo);

  const fromDayjs = toDayjs(registeredFrom);
  const toDayjsValue = toDayjs(registeredTo);
  const periodSuggestions = edition ? getEditionRegistrationPeriodDefaults(edition) : null;

  const handleFromChange = (value: Dayjs | null) => {
    onChange(
      EParticipantFilters.registeredFrom,
      value?.isValid() ? value.toISOString() : null,
    );
  };

  const handleToChange = (value: Dayjs | null) => {
    onChange(
      EParticipantFilters.registeredTo,
      value?.isValid() ? value.toISOString() : null,
    );
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
          gap: 1.5,
          alignItems: 'flex-start',
        }}
      >
        <MultiFilterSelect
          id="competition-filter"
          label={t('events.event')}
          value={competitionFilter}
          options={competitionOptions}
          onChange={handleCompetitionChange}
          clearAriaLabel={clearFilterLabel}
        />
        <MultiFilterSelect
          id="distance-filter"
          label={t('events.distance')}
          value={distanceFilter}
          options={distanceOptions}
          onChange={next => onChange(EParticipantFilters.distance, next)}
          clearAriaLabel={clearFilterLabel}
        />
        <MultiFilterSelect
          id="checkedIn-filter"
          label={t('participants.checkedIn')}
          value={checkedInFilter}
          options={checkedOptions}
          onChange={next => onChange(EParticipantFilters.checkedIn, next)}
          clearAriaLabel={clearFilterLabel}
        />
      </Box>

      <Accordion
        disableGutters
        elevation={0}
        expanded={advancedOpen}
        onChange={(_e, expanded) => setAdvancedOpen(expanded)}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="participant-advanced-filters"
          id="participant-advanced-filters-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">{t('participants.filters.advanced')}</Typography>
            {periodActive ? (
              <Chip size="small" color="primary" label={t('participants.filters.active')} />
            ) : null}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <AppDateTimePicker
              label={t('participants.filters.registeredFrom')}
              ampm={false}
              value={fromDayjs}
              onChange={handleFromChange}
              maxDateTime={toDayjsValue || undefined}
              slots={{ layout: FilterDateTimePickerLayout }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  margin: 'none',
                },
                field: {
                  clearable: true,
                  onClear: () => onChange(EParticipantFilters.registeredFrom, null),
                },
                shortcuts: {
                  items: periodSuggestions
                    ? [
                        {
                          label: t('participants.filters.suggestRegistrationOpen'),
                          getValue: () => dayjs(periodSuggestions.registeredFrom),
                        },
                      ]
                    : [],
                  changeImportance: 'accept',
                },
              }}
            />
            <AppDateTimePicker
              label={t('participants.filters.registeredTo')}
              ampm={false}
              value={toDayjsValue}
              onChange={handleToChange}
              minDateTime={fromDayjs || undefined}
              slots={{ layout: FilterDateTimePickerLayout }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  margin: 'none',
                },
                field: {
                  clearable: true,
                  onClear: () => onChange(EParticipantFilters.registeredTo, null),
                },
                shortcuts: {
                  items: periodSuggestions
                    ? [
                        {
                          label: t('participants.filters.suggestEventDate'),
                          getValue: () => dayjs(periodSuggestions.registeredTo),
                        },
                      ]
                    : [],
                  changeImportance: 'accept',
                },
              }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ParticipantFilterPopoverContent;
